// src/pages/HrDashboard/MatchingTile.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

// NEU: unsere Axios-Instanz + Session-Helper
import { mentoringApi as mApi } from '../../axiosInstance';
import { ensureMentoringSession } from '../../lib/session';

// Hilfsfunktion: Node-Buffer -> base64
function bufferToBase64(bufferObj) {
  if (bufferObj && bufferObj.type === 'Buffer' && Array.isArray(bufferObj.data)) {
    const uint8 = new Uint8Array(bufferObj.data);
    let binary = '';
    for (let i = 0; i < uint8.length; i++) binary += String.fromCharCode(uint8[i]);
    return window.btoa(binary);
  }
  return '';
}

function formatPct(v) {
  return `${Math.round(Number(v || 0))}%`;
}
function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString('de-DE', { timeZone: 'Europe/Berlin' });
  } catch {
    return iso;
  }
}

function MatchingTile({ tenantId, surveyId }) {
  const { t } = useTranslation();

  const [mentees, setMentees] = useState([]);
  const [mentors, setMentors] = useState([]);

  // kleine Übersichts-Stats aus /mentoring/match-ready/stats
  const [stats, setStats] = useState(null);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Slider (Fachlich-Anteil 0..100)
  const [weightProfile, setWeightProfile] = useState(50);

  // Aktive (server-gesperrte) Einstellung
  // { weightProfile: number, lockedAt: ISO, source?: string }
  const [activeCfg, setActiveCfg] = useState(null);

  // Abgeleitet: Ist Simulation aktiv (Slider ≠ Lock)?
  const simulationActive =
    !!activeCfg && Number(weightProfile) !== Number(activeCfg.weightProfile);

  // Daten für Kandidat:innen laden
  useEffect(() => {
    if (!tenantId || !surveyId) return;
    (async () => {
      // Session robust sicherstellen (Interceptors tun das zwar schon, das hier macht es deterministisch)
      try { await ensureMentoringSession(); } catch {}
      await loadData();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, surveyId]);

  // Aktive Konfiguration beim Mount/Survey-Wechsel lesen
  useEffect(() => {
    if (!surveyId) {
      setActiveCfg(null);
      return;
    }
    (async () => {
      try {
        const resp = await axios.get(
          `/matching/match/config/${surveyId}`,
          { withCredentials: true }
        );
        if (resp.data?.exists) {
          const wp = Number(resp.data.weightProfile) || 50;
          setActiveCfg({
            weightProfile: wp,
            lockedAt: resp.data.lockedAt,
            source: resp.data.source || 'server-locked',
          });
          // Slider visuell auf aktiven Wert setzen (UI = Backend)
          setWeightProfile(wp);
        } else {
          setActiveCfg(null);
        }
      } catch (e) {
        console.error('read active config failed', e);
        setActiveCfg(null);
      }
    })();
  }, [surveyId]);

  async function loadData() {
    setError('');
    setLoading(true);
    try {
      // 1) Ready-Infos (NEU: mApi + kurze Pfade)
      const readyResp = await mApi.get('/match-ready', {
        params: { survey_id: surveyId },
        withCredentials: true,
      });

      const statsResp = await mApi.get('/match-ready/stats', {
        params: { survey_id: surveyId },
        withCredentials: true,
      });
      setStats(statsResp.data || null);

      const readyMenteeIds = new Set((readyResp.data?.mentees || []).map(u => u.user_id));
      const readyMentorIds = new Set((readyResp.data?.mentors || []).map(u => u.user_id));

      // 2) Volle Datensätze holen (NEU: mApi)
      const [menteeResponse, mentorResponse] = await Promise.all([
        mApi.get('/available-mentees', {
          params: { tenant_id: tenantId, survey_id: surveyId },
          withCredentials: true,
        }),
        mApi.get('/available-mentors', {
          params: { tenant_id: tenantId, survey_id: surveyId },
          withCredentials: true,
        }),
      ]);

      // 3) Auf "ready" reduzieren
      const filteredMentees = (menteeResponse.data || []).filter(m => readyMenteeIds.has(m.user_id));
      const filteredMentors = (mentorResponse.data || []).filter(m => readyMentorIds.has(m.user_id));

      setMentees(filteredMentees);
      setMentors(filteredMentors);
    } catch (err) {
      console.error('MatchingTile loadData error:', err);
      setError(t('matching.fetchError', 'Error fetching available users'));
    } finally {
      setLoading(false);
    }
  }

  function renderAvatar(avatarData, avatarPath = '') {
    if (!avatarData && !avatarPath) {
      return (
        <div className="w-12 h-12 rounded-full bg-gray-600 flex items-center justify-center">?</div>
      );
    }

    let src = '';
    if (avatarPath) {
      src = `/mentoring/uploads/${avatarPath}`;
    } else if (typeof avatarData === 'string') {
      src = avatarData.startsWith('data:')
        ? avatarData
        : `data:image/png;base64,${avatarData}`;
    } else if (typeof avatarData === 'object') {
      src = `data:image/png;base64,${bufferToBase64(avatarData)}`;
    }

    return <img src={src} alt="Avatar" className="w-12 h-12 rounded-full object-cover" />;
  }

  async function handleMatchingStart() {
    // Mapped Payload – nur "ready" Nutzer:innen
    const mappedMentees = mentees.map(mentee => ({
      user_id: mentee.user_id,
      firstName: mentee.first_name,
      lastName: mentee.last_name,
      avatarPath: mentee.avatar_path,
      avatar:
        mentee.avatar?.startsWith?.('data:') || mentee.avatar?.startsWith?.('http')
          ? mentee.avatar
          : mentee.avatar_path
            ? `/mentoring/uploads/${mentee.avatar_path}`
            : null,
      abteilung: mentee.abteilung,
      experience: mentee.experience,
      job_title: mentee.job_title,
      goals: mentee.goals,
      competences: mentee.competences,
      bigfive_extraversion: mentee.bigfive_extraversion,
      bigfive_gewissenhaftigkeit: mentee.bigfive_gewissenhaftigkeit,
      bigfive_negative_emotionalitaet: mentee.bigfive_negative_emotionalitaet,
      bigfive_vertraeglichkeit: mentee.bigfive_vertraeglichkeit,
      bigfive_offenheit: mentee.bigfive_offenheit,
      tenant_id:         mentee.tenant_id,
      fach_tags:         mentee.fach_tags,
      application_fields: mentee.application_fields,
      methods_skills:    mentee.methods_skills,
      languages:         mentee.languages,
      mentor_preference: mentee.mentor_preference,
      mentor_background: mentee.mentor_background,
    }));

    const mappedMentors = mentors.map(mentor => ({
      user_id: mentor.user_id,
      firstName: mentor.first_name,
      lastName: mentor.last_name,
      avatarPath: mentor.avatar_path,
      avatar:
        mentor.avatar?.startsWith?.('data:') || mentor.avatar?.startsWith?.('http')
          ? mentor.avatar
          : mentor.avatar_path
            ? `/mentoring/uploads/${mentor.avatar_path}`
            : null,
      abteilung: mentor.abteilung,
      experience: mentor.experience,
      job_title: mentor.job_title,
      goals: mentor.goals,
      competences: mentor.competences,
      bigfive_extraversion: mentor.bigfive_extraversion,
      bigfive_gewissenhaftigkeit: mentor.bigfive_gewissenhaftigkeit,
      bigfive_negative_emotionalitaet: mentor.bigfive_negative_emotionalitaet,
      bigfive_vertraeglichkeit: mentor.bigfive_vertraeglichkeit,
      bigfive_offenheit: mentor.bigfive_offenheit,
      tenant_id:         mentor.tenant_id,
      fach_tags:         mentor.fach_tags,
      application_fields: mentor.application_fields,
      methods_skills:    mentor.methods_skills,
      languages:         mentor.languages,
      mentor_preference: mentor.mentor_preference,
      mentor_background: mentor.mentor_background,
    }));

    if (!mappedMentees.length || !mappedMentors.length) {
      alert(t('matching.noUsers', 'No mentees or mentors available.'));
      return;
    }

   // 👉 Simulation aktiv, wenn Slider ≠ aktiver Lock
      const isSimulation = !!simulationActive;

      const sid = Number(surveyId);
      if (!Number.isInteger(sid) || sid <= 0) {
        alert(t('matching.invalidSurvey', 'Ungültige Projekt/Umfrage-ID.'));
        return;
      }

      const payload = {
        surveyId: Number(surveyId),              // ✅ MUSS top-level rein
        mentors: mappedMentors,
        mentees: mappedMentees,
        weightProfile: Number(weightProfile),    // ✅ sauber als number
        matchingConfig: {
          weightProfile: Number(weightProfile),
          source: isSimulation ? 'frontend-simulation' : 'frontend-locked',
          lockedAt: new Date().toISOString(),
          surveyId: Number(surveyId),
          simulate: isSimulation
        }
      };

    try {
      const response = await axios.post('/matching/match', payload, { withCredentials: true });
      const resultId = response.data?.resultId;
      if (!resultId) {
        alert(t('matching.noResult', 'No matching result received'));
        return;
      }

      if (response.data?.config) {
        const cfg = response.data.config;
        setActiveCfg(prev => (prev ? { ...prev } : null));
        if (typeof cfg.weightProfile === 'number') {
          setWeightProfile(Number(cfg.weightProfile));
        }
      }

      const newUrl = `${window.location.origin}/#/matching-result?resultId=${resultId}&surveyId=${surveyId}`;
      window.open(newUrl, '_blank');
    } catch (err) {
      console.error('match error:', err);
      alert(t('matching.matchError', 'Error during matching'));
    }
  }

  const disableStart = loading || mentees.length === 0 || mentors.length === 0;

  return (
    <div className="bg-gray-800 p-6 rounded-2xl shadow-md mt-8">
      <h2 className="text-xl font-bold mb-6">{t('matching.heading', 'Matching Overview')}</h2>

      {error && <p className="text-red-500 mb-4">{error}</p>}

      {/* kleine Übersicht zu "ready" und Einzelkriterien */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-gray-700 p-4 rounded-xl">
            <h4 className="font-semibold mb-2">{t('matching.mentees', 'Mentees')}</h4>
            <div>{t('matching.ready', 'Ready')}: <b>{stats.mentee?.ready ?? 0}</b></div>
            <div>{t('matching.onboardingOk', 'Onboarding ok')}: {stats.mentee?.onboarding_ok ?? 0}</div>
            <div>{t('matching.analysisOk', 'Analysis ok')}: {stats.mentee?.analysis_ok ?? 0}</div>
            <div>{t('matching.profileOk', 'Profile ok')}: {stats.mentee?.profile_ok ?? 0}</div>
          </div>
          <div className="bg-gray-700 p-4 rounded-xl">
            <h4 className="font-semibold mb-2">{t('matching.mentors', 'Mentors')}</h4>
            <div>{t('matching.ready', 'Ready')}: <b>{stats.mentor?.ready ?? 0}</b></div>
            <div>{t('matching.onboardingOk', 'Onboarding ok')}: {stats.mentor?.onboarding_ok ?? 0}</div>
            <div>{t('matching.analysisOk', 'Analysis ok')}: {stats.mentor?.analysis_ok ?? 0}</div>
            <div>{t('matching.profileOk', 'Profile ok')}: {stats.mentor?.profile_ok ?? 0}</div>
          </div>
        </div>
      )}

      {/* Badge mit aktiver Einstellung (vom Server) */}
      {activeCfg && (
        <div
          className="mb-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm"
          style={{ background: '#eef', border: '1px solid #cde', color: '#1d2a44' }}
        >
          <strong>Aktive Einstellung:</strong>&nbsp;
          Persönlichkeit <b>{formatPct(100 - Number(activeCfg.weightProfile))}</b>
          <span className="opacity-70"> · seit {formatDate(activeCfg.lockedAt)}</span>
        </div>
      )}

      <div className="mb-2">
        <label htmlFor="weightRange" className="block mb-2">
          {t('matching.weighting', 'Weighting: Skills ↔ Personality')}
        </label>
        <div className="flex items-center">
          <input
            id="weightRange"
            type="range"
            min={30}
            max={70}
            value={weightProfile}
            onChange={(e) => setWeightProfile(Number(e.target.value))}
            className="apple-slider flex-1"
            style={{ '--progress': `${((weightProfile - 30) / 40) * 100}%` }}
          />
          <span className="ml-4 font-medium text-gray-100 whitespace-nowrap" style={{ minWidth: 160 }}>
            <span style={{ color: '#4189ff', fontWeight: 600 }}>{weightProfile}%</span> {t('matching.skills', 'Skills')}&nbsp;/&nbsp;
            <span style={{ color: '#2ebfae', fontWeight: 600 }}>{100 - weightProfile}%</span> {t('matching.personality', 'Personality')}
          </span>
        </div>
      </div>

      {/* Simulations-Hinweis + "Zurücksetzen"-Link */}
      {activeCfg && simulationActive && (
        <div className="mt-2 text-sm opacity-90">
          <span>
            {t('matching.simulationNote', 'Simulation: Änderungen gelten nicht für das laufende Programm.')}
          </span>
          <button
            type="button"
            onClick={() => setWeightProfile(activeCfg.weightProfile)}
            className="ml-2 underline"
          >
            {t('matching.resetToActive', 'Zur aktiven Einstellung zurücksetzen')}
          </button>
        </div>
      )}

      {/* kleiner Badge „Simulation“ rechts neben Button */}
      <div className="flex items-center gap-3 mb-8 mt-3">
        <button
          onClick={handleMatchingStart}
          disabled={disableStart}
          className={`bg-mentixxgreen hover:bg-green-400 text-black font-bold py-2 px-6 rounded-xl transition ${
            disableStart ? 'opacity-60 cursor-not-allowed' : ''
          }`}
          title={
            disableStart
              ? t('matching.disabledHint', 'Matching requires at least one ready mentee and mentor.')
              : undefined
          }
        >
          {loading ? t('matching.loading', 'Loading…') : t('matching.startButton', 'Start Matching')}
        </button>

        {simulationActive && (
          <span className="text-xs px-2 py-1 rounded-full" style={{ background: '#fff2cc', color: '#5a4300' }}>
            {t('matching.simulationBadge', 'Simulation')}
          </span>
        )}
      </div>

      {/* Mentees Liste */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4">{t('matching.mentees', 'Mentees')}</h3>
        {loading ? (
          <p className="text-gray-400">{t('matching.loading', 'Loading…')}</p>
        ) : mentees.length === 0 ? (
          <p className="text-gray-400">{t('matching.noMentees', 'No mentees found.')}</p>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {mentees.map((mentee) => (
              <li key={`mentee-${mentee.user_id}`} className="flex items-center bg-gray-700 p-4 rounded-lg">
                {renderAvatar(mentee.avatar, mentee.avatar_path)}
                <div className="ml-4">
                  <div className="font-semibold">
                    {mentee.first_name} {mentee.last_name}
                  </div>
                  <div className="text-gray-400 text-sm">
                    {mentee.abteilung || t('matching.noDepartment', 'No department')}
                  </div>
                  <div className="text-gray-400 text-sm">
                    {mentee.job_title || t('matching.noJobTitle', 'No job title')}
                  </div>
                  <div className="text-gray-400 text-sm">
                    {mentee.experience || t('matching.noExperience', 'No experience')}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Mentors Liste */}
      <div>
        <h3 className="text-lg font-semibold mb-4">{t('matching.mentors', 'Mentors')}</h3>
        {loading ? (
          <p className="text-gray-400">{t('matching.loading', 'Loading…')}</p>
        ) : mentors.length === 0 ? (
          <p className="text-gray-400">
            {t('matching.noMentors', 'No mentors found.')}
          </p>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {mentors.map((mentor) => (
              <li key={`mentor-${mentor.user_id}`} className="flex items-center bg-gray-700 p-4 rounded-lg">
                {renderAvatar(mentor.avatar, mentor.avatar_path)}
                <div className="ml-4">
                  <div className="font-semibold">
                    {mentor.first_name} {mentor.last_name}
                  </div>
                  <div className="text-gray-400 text-sm">
                    {mentor.abteilung || t('matching.noDepartment', 'No department')}
                  </div>
                  <div className="text-gray-400 text-sm">
                    {mentor.job_title || t('matching.noJobTitle', 'No job title')}
                  </div>
                  <div className="text-gray-400 text-sm">
                    {mentor.experience || t('matching.noExperience', 'No experience')}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default MatchingTile;
